/**
 * Route Builder Utility
 * Phase 0: Route URL Alignment
 *
 * Type-safe route generation for standardized URL structure:
 * /{portalType}/:workspaceId/:year/{domain}/{entity}/{action}
 *
 * V2 Convention:
 * - LIST routes use plural slug:  /portal/:ws/:year/finance/budgets
 * - NEW routes use singular slug:  /portal/:ws/:year/finance/budget/new
 * - ITEM routes use singular slug: /portal/:ws/:year/finance/budget/:id
 *
 * See docs/SAAS_ROUTE_CONVENTION_V2_PLAN.md for full specification.
 *
 * Usage:
 *   import { routes } from '@/utils/route_builder'
 *   history.push(routes.parameter.teams.view(workspaceId, teamId))
 */

import { parseRuntimeRoutePath } from '@/utils/runtime_route_paths'

export type PortalType = 'portal' | 'adminportal' | 'make' | 'marketplace' | (string & {})

/**
 * Generic entity route builder following the v2 convention.
 *
 * @param config.portalType - 'portal' or 'adminportal'
 * @param config.workspaceId - workspace identifier
 * @param config.year - fiscal year (omit/null for workspace-scoped entities)
 * @param config.domain - domain code (e.g. 'finance', 'parameter')
 * @param config.slugSingular - singular entity slug (e.g. 'budget')
 * @param config.slugPlural - plural entity slug (e.g. 'budgets')
 * @returns object with list(), new(), item() route builders
 */
export type EntityRouteConfig = {
  portalType: PortalType;
  workspaceId: string;
  year?: string | null;
  domain: string;
  slugSingular: string;
  slugPlural: string;
};

export const buildEntityRoutes = (config: EntityRouteConfig) => {
  const { portalType, workspaceId, year, domain, slugSingular, slugPlural } = config;
  const base = year
    ? `/${portalType}/${workspaceId}/${year}/${domain}`
    : `/${portalType}/${workspaceId}/${domain}`;

  return {
    list: `${base}/${slugPlural}`,
    new: `${base}/${slugSingular}/new`,
    item: (id: string) => `${base}/${slugSingular}/${id}`,
  };
};

const PORTAL_PREFIXES: readonly PortalType[] = ['portal', 'adminportal', 'make', 'marketplace']

const isPortalPrefix = (segment?: string): segment is PortalType =>
  segment != null && PORTAL_PREFIXES.includes(segment as PortalType)

/**
 * Route builder for Parameter module (no year context)
 */
export const parameterRoutes = {
  teams: {
    list: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/parameter/teams`,
    new: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/parameter/team/new`,
    view: (portalType: PortalType, workspaceId: string, id: string) =>
      `/${portalType}/${workspaceId}/parameter/team/${id}`,
  },
  glaccounts: {
    list: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/parameter/glaccounts`,
    new: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/parameter/glaccount/new`,
    view: (portalType: PortalType, workspaceId: string, id: string) =>
      `/${portalType}/${workspaceId}/parameter/glaccount/${id}`,
  },
  cas: {
    list: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/parameter/cas`,
    new: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/parameter/ca/new`,
    view: (portalType: PortalType, workspaceId: string, id: string) =>
      `/${portalType}/${workspaceId}/parameter/ca/${id}`,
  },
  parameters: {
    list: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/parameter/parameters`,
    new: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/parameter/parameter/new`,
    view: (portalType: PortalType, workspaceId: string, id: string) =>
      `/${portalType}/${workspaceId}/parameter/parameter/${id}`,
  },
  auditlogs: {
    list: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/parameter/auditlogs`,
    new: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/parameter/auditlog/new`,
    view: (portalType: PortalType, workspaceId: string, id: string) =>
      `/${portalType}/${workspaceId}/parameter/auditlog/${id}`,
  },
  fileloadings: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/parameter/fileloadings`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/parameter/fileloading/new`,
    view: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/parameter/fileloading/${id}`,
  },
  fileexports: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/parameter/fileexports`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/parameter/fileexport/new`,
    view: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/parameter/fileexport/${id}`,
  },
  integrationJobs: {
    list: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/parameter/integration-jobs`,
    new: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/parameter/integration-job/new`,
    view: (portalType: PortalType, workspaceId: string, id: string) =>
      `/${portalType}/${workspaceId}/parameter/integration-job/${id}`,
    history: (portalType: PortalType, workspaceId: string, id: string) =>
      `/${portalType}/${workspaceId}/parameter/integration-job-history/${id}`,
  },
}

/**
 * Route builder for Finance module (with year context)
 */
export const financeRoutes = {
  budgets: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgets`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budget/new`,
    view: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budget/${id}`,
  },
  budgetlines: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgetlines`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgetline/new`,
    view: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgetline/${id}`,
  },
  budgetlinedetails: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgetlinedetails`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgetlinedetail/new`,
    view: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgetlinedetail/${id}`,
  },
  invoices: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/invoices`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/invoice/new`,
    view: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/invoice/${id}`,
  },
  budgetrequests: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgetrequests`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgetrequest/new`,
    view: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgetrequest/${id}`,
  },
  budgetrequestextras: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgetrequestextras`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgetrequestextra/new`,
    view: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/finance/budgetrequestextra/${id}`,
  },
  suppliers: {
    list: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/finance/suppliers`,
    new: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/finance/supplier/new`,
    view: (portalType: PortalType, workspaceId: string, id: string) =>
      `/${portalType}/${workspaceId}/finance/supplier/${id}`,
  },
}

/**
 * Route builder for PMO module (with year context)
 */
export const pmoRoutes = {
  projects: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/pmo/projects`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/pmo/project/new`,
    view: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/pmo/project/${id}`,
  },
  projectreportings: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/pmo/projectreportings`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/pmo/projectreporting/new`,
    view: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/pmo/projectreporting/${id}`,
  },
}

/**
 * Route builder for Admin module (no year)
 */
export const adminRoutes = {
  users: {
    list: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/admin/users`,
    new: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/admin/user/new`,
    view: (portalType: PortalType, workspaceId: string, id: string) =>
      `/${portalType}/${workspaceId}/admin/user/${id}`,
  },
  roles: {
    list: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/admin/roles`,
    new: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/admin/role/new`,
    view: (portalType: PortalType, workspaceId: string, id: string) =>
      `/${portalType}/${workspaceId}/admin/role/${id}`,
  },
  groups: {
    list: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/admin/groups`,
    new: (portalType: PortalType, workspaceId: string) =>
      `/${portalType}/${workspaceId}/admin/group/new`,
    view: (portalType: PortalType, workspaceId: string, id: string) =>
      `/${portalType}/${workspaceId}/admin/group/${id}`,
  },
}

/**
 * Route builder for Sourcing module (year-scoped)
 * V2 Convention: plural for list, singular for new/item
 */
export const sourcingRoutes = {
  supplier: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/sourcing/suppliers`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/sourcing/supplier/new`,
    item: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/sourcing/supplier/${id}`,
  },
  supplierconsumption: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/sourcing/supplierconsumptions`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/sourcing/supplierconsumption/new`,
    item: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/sourcing/supplierconsumption/${id}`,
  },
  contract: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/sourcing/contracts`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/sourcing/contract/new`,
    item: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/sourcing/contract/${id}`,
  },
  servicetype: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/sourcing/servicetypes`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/sourcing/servicetype/new`,
    item: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/sourcing/servicetype/${id}`,
  },
}

/**
 * Route builder for CBM module (year-scoped)
 * V2 Convention: plural for list, singular for new/item
 */
export const cbmRoutes = {
  bcm: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/cbm/bcms`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/cbm/bcm/new`,
    item: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/cbm/bcm/${id}`,
  },
  beal: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/cbm/beals`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/cbm/beal/new`,
    item: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/cbm/beal/${id}`,
  },
  seal: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/cbm/seals`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/cbm/seal/new`,
    item: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/cbm/seal/${id}`,
  },
  sla: {
    list: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/cbm/slas`,
    new: (portalType: PortalType, workspaceId: string, year: string) =>
      `/${portalType}/${workspaceId}/${year}/cbm/sla/new`,
    item: (portalType: PortalType, workspaceId: string, year: string, id: string) =>
      `/${portalType}/${workspaceId}/${year}/cbm/sla/${id}`,
  },
}

/**
 * Consolidated route builder
 * Main export for application-wide use
 */
export const routes = {
  parameter: parameterRoutes,
  finance: financeRoutes,
  pmo: pmoRoutes,
  sourcing: sourcingRoutes,
  cbm: cbmRoutes,
  admin: adminRoutes,

  // Module pages (no entity)
  modules: {
    parameter: (portalType: PortalType) => `/${portalType}/parameter`,
    finance: (portalType: PortalType) => `/${portalType}/finance`,
    pmo: (portalType: PortalType) => `/${portalType}/pmo`,
    sourcing: (portalType: PortalType) => `/${portalType}/sourcing`,
    cbm: (portalType: PortalType) => `/${portalType}/cbm`,
    admin: (portalType: PortalType) => `/${portalType}/admin`,
  },

  // Portal home pages
  home: {
    root: (portalType: PortalType) => `/${portalType}`,
    portal: () => '/portal',
    adminportal: () => '/adminportal',
    make: () => '/make',
  },

  // Make workspace
  make: {
    home: () => '/make',
    settings: () => '/make/settings',
    stores: () => '/make/settings/stores',
    routes: () => '/make/settings/routes',
    translations: () => '/make/settings/translations',
    fields: () => '/make/settings/fields',
    builder: () => '/make/settings/builder',
    library: () => '/make/settings/library',
  },

  // Public routes
  public: {
    login: () => '/login',
    register: () => '/register',
    about: () => '/about',
    terms: () => '/terms',
    privacy: () => '/privacy',
  },

  // Other
  marketplace: () => '/marketplace',
  documentation: () => '/documentation',
  profile: () => '/profile',
  settings: () => '/settings',
}

const getPathSegments = (pathname: string): string[] => pathname.split('/').filter(Boolean)

export function getPortalPrefixFromPath(pathname: string, fallback: PortalType = 'portal'): PortalType {
  const [rootSegment] = getPathSegments(pathname)
  if (isPortalPrefix(rootSegment)) {
    return rootSegment
  }

  const parsedRoute = parseRuntimeRoutePath(pathname, fallback)
  if (parsedRoute.matched) {
    return parsedRoute.portalPrefix as PortalType
  }

  return fallback
}

export function getPortalRootFromPath(pathname: string, fallback: PortalType = 'portal'): string {
  return `/${getPortalPrefixFromPath(pathname, fallback)}`
}

/**
 * Helper: Get portal type from current route
 */
export function getPortalTypeFromPath(pathname: string): PortalType {
  return getPortalPrefixFromPath(pathname, 'portal')
}

/**
 * Helper: Extract workspace ID from current route
 * Returns null if not found
 */
export function getWorkspaceIdFromPath(pathname: string): string | null {
  const parsedRoute = parseRuntimeRoutePath(pathname)
  if (parsedRoute.matched && parsedRoute.workspaceId) {
    return parsedRoute.workspaceId
  }

  // Preferred pattern: /{portal|adminportal}/:workspaceId/...
  const portalMatch = pathname.match(/^\/(portal|adminportal)\/([^/]+)/)
  if (portalMatch) {
    return portalMatch[2]
  }

  // Legacy pattern still used by some migrated pages: /:workspaceId/:year/...
  const legacyMatch = pathname.match(/^\/([^/]+)\/(\d{4})(?:\/|$)/)
  return legacyMatch ? legacyMatch[1] : null
}

/**
 * Helper: Extract year from current route
 * Returns null if not found
 */
export function getYearFromPath(pathname: string): string | null {
  const parsedRoute = parseRuntimeRoutePath(pathname)
  if (parsedRoute.matched && parsedRoute.year) {
    return parsedRoute.year
  }

  // Preferred pattern: /{portal|adminportal}/:workspaceId/:year/...
  const portalMatch = pathname.match(/^\/(portal|adminportal)\/[^/]+\/(\d{4})(?:\/|$)/)
  if (portalMatch) {
    return portalMatch[2]
  }

  // Legacy pattern still used by some migrated pages: /:workspaceId/:year/...
  const legacyMatch = pathname.match(/^\/[^/]+\/(\d{4})(?:\/|$)/)
  return legacyMatch ? legacyMatch[1] : null
}

/**
 * Helper: Build route with automatic context detection
 * Useful for redirects where context is already known
 */
export function buildRouteWithContext(
  pathname: string,
  routeBuilder: (portalType: PortalType, workspaceId: string, ...args: any[]) => string,
  ...additionalArgs: any[]
): string {
  const portalType = getPortalTypeFromPath(pathname)
  const workspaceId = getWorkspaceIdFromPath(pathname)

  if (!workspaceId) {
    throw new Error(`Cannot build route: workspaceId not found in path ${pathname}`)
  }

  return routeBuilder(portalType, workspaceId, ...additionalArgs)
}

/**
 * Helper: Build route with year context
 */
export function buildRouteWithYearContext(
  pathname: string,
  routeBuilder: (portalType: PortalType, workspaceId: string, year: string, ...args: any[]) => string,
  ...additionalArgs: any[]
): string {
  const portalType = getPortalTypeFromPath(pathname)
  const workspaceId = getWorkspaceIdFromPath(pathname)
  const year = getYearFromPath(pathname)

  if (!workspaceId) {
    throw new Error(`Cannot build route: workspaceId not found in path ${pathname}`)
  }

  if (!year) {
    throw new Error(`Cannot build route: year not found in path ${pathname}`)
  }

  return routeBuilder(portalType, workspaceId, year, ...additionalArgs)
}

const PORTAL_SCOPE_PATTERN = /^\/(portal|adminportal)\/([^/]+)/
const FOUR_DIGIT_YEAR = /^\d{4}$/

/**
 * Path of a year-scoped entity record (`/{portal}/{ws}/{year}/{domain}/{entity}/{id}`)
 * built from the CURRENT page (UAT 17.09, liste des budgets sans année).
 *
 * The drawers of the finance lists (budgets, invoices) used to require the year
 * in the page URL (`/^\/(portal|adminportal)\/([^/]+)\/(\d{4})\//`): opened from
 * `/portal/default/finance/budgets` (runtime route, no year) the "open full
 * form" button did nothing and the embedded full form (with its READ / EDIT
 * action menu) was replaced by the bare fast-track form.
 *
 * The year of the URL still wins (unchanged behaviour); otherwise
 * `fallbackYears` (the record's year, then the page year) when it is a 4-digit
 * year, otherwise the current calendar year. Returns `null` outside a
 * `/portal|adminportal/{ws}/...` page.
 */
export function buildYearScopedItemPath(
  pathname: string,
  domain: string,
  entity: string,
  id: string | number,
  ...fallbackYears: Array<string | number | null | undefined>
): string | null {
  const scope = PORTAL_SCOPE_PATTERN.exec(pathname)
  if (!scope) {
    return null
  }
  const [, portalPrefix, workspaceId] = scope
  const fallback = fallbackYears
    .map((value) => (value == null ? '' : String(value).trim()))
    .find((value) => FOUR_DIGIT_YEAR.test(value))
  const year = getYearFromPath(pathname) ?? fallback ?? String(new Date().getFullYear())
  return `/${portalPrefix}/${workspaceId}/${year}/${domain}/${entity}/${id}`
}
